import { FC, useContext, useEffect, useState } from "react";
import { Controller } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../context/notificationContext";
import useI18n from "../../../../../hooks/useTranslations";
import { useApi } from "../../../../../http/api";
import {
  AdminPatrolSystem,
  InsertAdminPatrolSystemBody,
  UpdateRoofMaterialNewImageBody,
  UpdateRoofMaterialRemoveImageBody,
} from "../../../../../http/patrol/types/patrolSystems";
import { usePatrolSystemsApi } from "../../../../../http/patrol/usePatrolSystems";
import Form from "../../../../ui/Form";
import FormField from "../../../../ui/FormField";
import FormFieldsContainer from "../../../../ui/FormFieldsContainer";
import useImageUploader from "../../../../ui/ImageUploader";
import Input from "../../../../ui/Input";
import Numeric from "../../../../ui/Numeric";
import Switch from "../../../../ui/Switch";
import VisibilityScope, { VisibilityScopeValue } from "../../../../ui/VisibilityScope";
import { CustomSelect } from "../../../../ui/select/CustomSelect";

type systemTypeValues = "only-life-line" | "only-anchor-point" | "both";

type systemType = {
  value: systemTypeValues;
  title: string;
};

export interface SystemFormBody {
  name: string | null;
  order: number | null;
  visibility: VisibilityScopeValue;
  insulationThicknessRequired: boolean;
  waveSpanRequired: boolean;
  supportMaterialRequired: boolean;
  supportRequired: boolean;
  stepHeightRequired: boolean;
  isSubstructureIndependent: boolean;
  isClampable: boolean;
  maxCornersPerLifeline: number | null;
  type: systemTypeValues;
}

interface SystemFormProps {
  isLoading?: boolean;
  isDisabled?: boolean;
  patrolSystem?: AdminPatrolSystem;
  onUpdate?: (isAnchorPoint: boolean, isLifeLine: boolean, isClampable: boolean) => void;
}

const SystemForm: FC<SystemFormProps> = ({ isLoading = false, isDisabled, patrolSystem, onUpdate }) => {
  const i18n = useI18n();
  const { systemId } = useParams<{ systemId: string }>();
  const navigate = useNavigate();
  const dispatch = useContext(NotificationDispatch);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const patrolSystemsApi = usePatrolSystemsApi();
  const api = useApi();

  const { component, newImage, removeImage, onSave } = useImageUploader({
    defaultImage: patrolSystem?.image,
    isLoading: isLoading && !patrolSystem,
    disabled: isDisabled,
  });

  const onSubmit = () => async (formData: SystemFormBody) => {
    setIsSubmitting(true);

    const common: InsertAdminPatrolSystemBody = {
      name: formData.name?.trim() ?? "",
      order: formData.order!,
      visibility: formData.visibility,
      insulationThicknessRequired: formData.insulationThicknessRequired,
      waveSpanRequired: formData.waveSpanRequired,
      supportMaterialRequired: formData.supportMaterialRequired,
      supportRequired: formData.supportRequired,
      stepHeightRequired: formData.stepHeightRequired,
      isLifeLine: formData.type === "both" || formData.type === "only-anchor-point",
      isAnchorPoint: formData.type === "both" || formData.type === "only-life-line",
      isClampable: formData.isClampable,
      maxCornersPerLifeline: formData.maxCornersPerLifeline ?? undefined,
      isSubstructureIndependent: formData.isSubstructureIndependent,
    };

    if (patrolSystem?.id) {
      const update = async (body: UpdateRoofMaterialRemoveImageBody | UpdateRoofMaterialNewImageBody) => {
        try {
          await patrolSystemsApi.updateAdminPatrolSystem(patrolSystem?.id!, body);
          dispatch(showSuccessNotification(i18n.translation));
          onSave();
          onUpdate?.(common.isLifeLine, common.isLifeLine, common.isClampable);
        } catch (err) {
          dispatch(showErrorNotification(i18n, err));
        } finally {
          setIsSubmitting(false);
        }
      };
      if (newImage) {
        try {
          const signedUrl = await patrolSystemsApi.generateUploadImageUrl(
            patrolSystem?.id!,
            newImage.name,
            newImage.type,
          );
          const res = await api.uploadFile(signedUrl, newImage);
          if (!res.ok) {
            dispatch(showErrorNotification(i18n, res));
            setIsSubmitting(false);
            return;
          }
          const body: UpdateRoofMaterialNewImageBody = {
            ...common,
            image: newImage.name,
          };
          await update(body);
        } catch (error) {
          dispatch(showErrorNotification(i18n, error));
        } finally {
          setIsSubmitting(false);
        }
      } else {
        const body: UpdateRoofMaterialRemoveImageBody = {
          ...common,
          removeImage: removeImage,
        };
        await update(body);
      }
    } else {
      const redirect = (id: string) => {
        setIsSubmitting(false);
        navigate(`/admin/systems/${systemId}/patrol_systems/${id}`);
        onSave();
      };
      const body: InsertAdminPatrolSystemBody = {
        ...common,
        image: newImage?.name,
      };

      try {
        const res = await patrolSystemsApi.insertAdminPatrolSystem(body);
        if (newImage) {
          const signedUrl = await patrolSystemsApi.generateUploadImageUrl(res.id, newImage.name, newImage.type);
          const uploadResponse = await api.uploadFile(signedUrl, newImage);
          if (!uploadResponse.ok) {
            dispatch(showErrorNotification(i18n, res));
            return;
          }
          redirect(res.id);
        } else {
          redirect(res.id);
        }
      } catch (error) {
        dispatch(showErrorNotification(i18n, error));
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const systemTypes: systemType[] = [
    {
      value: "only-life-line",
      title: i18n.translation.common.onlyLifeLine,
    },
    {
      value: "only-anchor-point",
      title: i18n.translation.common.onlyAnchorPoint,
    },
    {
      value: "both",
      title: i18n.translation.common.lifeLinePlusAnchorPoint,
    },
  ];

  return (
    <Form<SystemFormBody>
      defaultValues={
        isLoading
          ? undefined
          : {
              name: null,
              order: 50,
              visibility: "hidden",
              type: "only-life-line",
            }
      }
      submitText={i18n.translation.common.save}
      disabled={isDisabled || isLoading || isSubmitting}
      onSubmit={onSubmit}
    >
      {({ register, formState: { errors }, control, setValue, watch, reset }) => {
        const type = watch("type");
        const insulationThicknessRequired = !!watch("insulationThicknessRequired");
        const waveSpanRequired = !!watch("waveSpanRequired");
        const supportMaterialRequired = !!watch("supportMaterialRequired");
        const supportRequired = !!watch("supportRequired");
        const stepHeightRequired = !!watch("stepHeightRequired");
        const isSubstructureIndependent = !!watch("isSubstructureIndependent");
        const isClampable = !!watch("isClampable");

        useEffect(() => {
          patrolSystem &&
            reset({
              ...patrolSystem,
              order: patrolSystem?.order ?? 50,
              type:
                patrolSystem.isLifeLine && patrolSystem.isAnchorPoint
                  ? "both"
                  : patrolSystem.isAnchorPoint
                    ? "only-anchor-point"
                    : "only-life-line",
            });
        }, [patrolSystem]);

        return (
          <FormFieldsContainer imageUploader={component}>
            <FormField label={i18n.translation.common.name} error={errors.name}>
              {({ labelId, isInvalid, isOptional }) => (
                <Input
                  id={labelId}
                  isInvalid={isInvalid}
                  disabled={isDisabled || isLoading || isSubmitting}
                  {...register("name", {
                    required: !isOptional,
                    minLength: 1,
                  })}
                />
              )}
            </FormField>

            <Controller
              name="type"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.type} error={fieldState.error}>
                  {({ labelId }) => (
                    <CustomSelect<systemType>
                      {...field}
                      inputId={labelId}
                      options={systemTypes}
                      isDisabled={isDisabled || isLoading || isSubmitting}
                      isLoading={isLoading}
                      getOptionValue={(x) => x.value}
                      getOptionLabel={(x) => x.title}
                      value={systemTypes.find((option) => option.value === field.value) ?? null}
                      onChange={(option) =>
                        setValue("type", systemTypes.find((s) => s.value === option?.value)?.value!)
                      }
                    />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="maxCornersPerLifeline"
              control={control}
              disabled={isDisabled || isLoading || isSubmitting}
              render={({ field, fieldState }) => (
                <FormField
                  label={i18n.translation.common.maxCornersPerLifeline}
                  error={fieldState.error}
                  hidden={type === "only-anchor-point"}
                  isOptional
                >
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading || isSubmitting}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("maxCornersPerLifeline", value)}
                    />
                  )}
                </FormField>
              )}
            />
            <FormField
              label={i18n.translation.common.maxCornersPerLifeline}
              isOptional
              hidden={type !== "only-anchor-point"}
            >
              <Input disabled />
            </FormField>
            <FormField label={i18n.translation.common.insulationThicknessRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={insulationThicknessRequired}
                  onChange={(checked) => setValue("insulationThicknessRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.waveSpanRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={waveSpanRequired}
                  onChange={(checked) => setValue("waveSpanRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.supportMaterialRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={supportMaterialRequired}
                  onChange={(checked) => setValue("supportMaterialRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.supportRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={supportRequired}
                  onChange={(checked) => setValue("supportRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.stepHeightRequired} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={stepHeightRequired}
                  onChange={(checked) => setValue("stepHeightRequired", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isSubstructureIndependent} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isSubstructureIndependent}
                  onChange={(checked) => setValue("isSubstructureIndependent", checked)}
                />
              )}
            </FormField>
            <FormField label={i18n.translation.common.isClampable} isOptional>
              {({ labelId }) => (
                <Switch
                  disabled={isDisabled || isLoading || isSubmitting}
                  id={labelId}
                  checked={isClampable}
                  onChange={(checked) => setValue("isClampable", checked)}
                />
              )}
            </FormField>
            <Controller
              name="visibility"
              control={control}
              rules={{ required: true }}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                  {({ labelId }) => (
                    <VisibilityScope {...field} inputId={labelId} onChange={(value) => setValue("visibility", value)} />
                  )}
                </FormField>
              )}
            />
            <Controller
              name="order"
              control={control}
              rules={{ required: true, min: 1 }}
              disabled={isDisabled || isLoading || isSubmitting}
              render={({ field, fieldState }) => (
                <FormField label={i18n.translation.common.order} error={fieldState.error}>
                  {({ labelId, isInvalid }) => (
                    <Numeric
                      {...field}
                      id={labelId}
                      isInvalid={isInvalid}
                      disabled={isDisabled || isLoading || isSubmitting}
                      decimalScale={0}
                      allowNegative={false}
                      onBlur={(value) => setValue("order", value)}
                    />
                  )}
                </FormField>
              )}
            />
          </FormFieldsContainer>
        );
      }}
    </Form>
  );
};

export default SystemForm;
